import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Paper,
  CircularProgress,
  TextField,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { ContentWrapper } from '../../shared/ContentWrapper/ContentWrapper';
import {
  getOrderDetail,
  nextOrderStatus,
  markProductAsTaken,
  deleteOrderProduct,
  payOrder,
  uploadExtraReceipts,
} from '../../api/shopperOrders';
import {
  setCurrentOrder,
  setLoading,
  setError,
  toggleProductTakenLocal,
} from '../../features/shopperOrders/shopperOrdersSlice';
import { getToken } from '../../services/auth';

export const OrderDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentOrder, loading, error } = useSelector(
    (state) => state.shopperOrders,
  );
  const [receiptBase64, setReceiptBase64] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [extraReceipts, setExtraReceipts] = useState([]);
  const [totalValue, setTotalValue] = useState('');
  const [taxes, setTaxes] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isPaidLocal, setIsPaidLocal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);

  const fetchDetail = async () => {
    dispatch(setLoading(true));
    try {
      console.log('Fetching order detail for ID:', id);
      const response = await getOrderDetail({ orderId: id, token: getToken() });
      console.log('Order detail response:', response);
      dispatch(setCurrentOrder(response));
    } catch (err) {
      console.error('Error fetching order detail:', err);
      dispatch(setError(err.message || 'Error loading order'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  const handleNextStatus = async () => {
    setIsUpdatingStatus(true);
    try {
      await nextOrderStatus({ orderId: id, token: getToken() });
      window.location.reload();
    } catch (err) {
      let message = t(
        'ShopperOrders.errorNextStatus',
        'Error advancing status',
      );
      if (err.response && err.response.data && err.response.data.detail) {
        message = err.response.data.detail;
      } else if (err.message) {
        message = err.message;
      }
      alert(message);
      setIsUpdatingStatus(false);
    }
  };

  const handleToggleTaken = async (item) => {
    const orderBranchProductId = item.storeBranchProduct || item.id;
    // Optimistic UI update
    dispatch(toggleProductTakenLocal(orderBranchProductId));
    try {
      await markProductAsTaken({
        orderId: id,
        orderBranchProductId: orderBranchProductId,
        storeBranchProduct: item.storeBranchProduct,
        quantity: item.quantity,
        token: getToken(),
      });
      // Opcional: recargar para asegurar sincronización con el backend
      fetchDetail();
    } catch (err) {
      // Revertir en caso de error
      dispatch(toggleProductTakenLocal(orderBranchProductId));
      alert('Error marking product: ' + err.message);
    }
  };

  const handleDeleteProduct = async (orderBranchProductId) => {
    if (
      window.confirm(
        t(
          'ShopperOrders.confirmDelete',
          'Are you sure you want to remove this product?',
        ),
      )
    ) {
      try {
        await deleteOrderProduct({
          orderId: id,
          orderBranchProductId,
          token: getToken(),
        });
        fetchDetail();
      } catch (err) {
        alert('Error deleting product: ' + err.message);
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        var base64 = reader.result;
        if (base64 && typeof base64 === 'string') {
          setReceiptBase64(base64.split(',')[1]);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePayment = () => {
    if (totalValue === '' || isNaN(parseFloat(totalValue))) {
      alert('Please enter a valid total value');
      return;
    }
    if (taxes === '' || isNaN(parseFloat(taxes))) {
      alert('Please enter a valid taxes value');
      return;
    }
    setShowPaymentConfirmation(true);
  };

  const executePayment = async () => {
    try {
      await payOrder({
        orderId: id,
        total_value: parseFloat(totalValue).toFixed(2),
        taxes: parseFloat(taxes).toFixed(2),
        receipt: receiptFile,
        token: getToken(),
      });
      alert('Payment processed successfully');
      setIsPaidLocal(true);
      fetchDetail();
    } catch (err) {
      alert('Error processing payment: ' + err.message);
    }
  };

  const handleExtraFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const readers = files.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          var res = reader.result;
          if (res && typeof res === 'string') {
            resolve(res.split(',')[1]);
          } else {
            resolve('');
          }
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((results) => {
      setExtraReceipts(results);
    });
  };

  const handleUploadExtra = async () => {
    if (extraReceipts.length === 0) {
      alert('Please select extra receipts');
      return;
    }
    try {
      await uploadExtraReceipts({
        orderId: id,
        images: extraReceipts,
        token: getToken(),
      });
      alert('Extra receipts uploaded successfully');
      setExtraReceipts([]);
    } catch (err) {
      alert('Error uploading extra receipts: ' + err.message);
    }
  };

  if (loading && !currentOrder) {
    return (
      <ContentWrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%">
          <CircularProgress />
        </Box>
      </ContentWrapper>
    );
  }

  if (error && !currentOrder) {
    return (
      <ContentWrapper>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <Button onClick={fetchDetail}>
          {t('ShopperOrders.retry', 'Retry')}
        </Button>
      </ContentWrapper>
    );
  }

  if (!currentOrder) return null;

  // Extraer productos desde la estructura branchStores
  const branchStores = currentOrder.branchStores || [];
  let products = [];
  branchStores.forEach((store) => {
    if (store.products && Array.isArray(store.products)) {
      products = products.concat(store.products);
    }
  });

  const statusObj = currentOrder.status;
  const statusText =
    statusObj && typeof statusObj === 'object'
      ? statusObj.description || 'N/A'
      : statusObj || 'N/A';
  const statusCode =
    statusObj && typeof statusObj === 'object'
      ? statusObj.name || statusObj.code || statusObj.description || ''
      : statusObj || '';

  const isSelectingProducts =
    statusCode === 'SELECTING_PRODUCTS' || statusText === 'SELECTING_PRODUCTS';
  const isPaying = statusCode === 'PAYING' || statusText === 'PAYING';

  const isOrderPaid = currentOrder.isPaid || currentOrder.paid || isPaidLocal;

  return (
    <ContentWrapper>
      <Box
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <Typography variant="h4">
          {t('ShopperOrders.orderDetail', 'Order Detail')} #
          {currentOrder.reference || id}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleNextStatus}
          disabled={isUpdatingStatus}>
          {isUpdatingStatus ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('ShopperOrders.nextStatus', 'Next Status')
          )}
        </Button>
      </Box>

      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <Typography variant="h6">
          {t('ShopperOrders.info', 'General Info')}
        </Typography>
        <Typography>
          {t('ShopperOrders.status', 'Status')}: {statusText}
        </Typography>

        {currentOrder.order_type === 'DELIVERY' ||
        currentOrder.deliveryAddress ? (
          <Typography>
            {t('ShopperOrders.deliveryAddress', 'Delivery Address')}:{' '}
            {(currentOrder.deliveryAddress &&
              currentOrder.deliveryAddress.address &&
              currentOrder.deliveryAddress.address.address) ||
              (currentOrder.deliveryAddress &&
                currentOrder.deliveryAddress.address &&
                currentOrder.deliveryAddress.address.description) ||
              'N/A'}
          </Typography>
        ) : null}

        <Typography>
          {t('ShopperOrders.shopper', 'Shopper')}:{' '}
          {currentOrder.shopper && currentOrder.shopper.user
            ? `${currentOrder.shopper.user.firstName} ${currentOrder.shopper.user.lastName}`
            : t('ShopperOrders.unassigned', 'Unassigned')}
        </Typography>

        {currentOrder.client && currentOrder.client.user && (
          <Box mt={1}>
            <Typography variant="subtitle2">
              {t('ShopperOrders.client', 'Client Info')}
            </Typography>
            <Typography variant="body2">
              {currentOrder.client.user.firstName}{' '}
              {currentOrder.client.user.lastName}
            </Typography>
            <Typography variant="body2">
              {currentOrder.client.user.phoneNumber}
            </Typography>
            <Typography variant="body2">
              {currentOrder.client.user.email}
            </Typography>
          </Box>
        )}

        {currentOrder.thirdPartyDeliveryInfo &&
          currentOrder.thirdPartyDeliveryInfo.trackUrl && (
            <Box mt={2}>
              <Typography variant="subtitle2">
                {t('ShopperOrders.trackDelivery', 'Track Delivery')}{' '}
                {currentOrder.thirdPartyDeliveryInfo.name
                  ? `(${currentOrder.thirdPartyDeliveryInfo.name})`
                  : ''}
              </Typography>
              <a
                href={currentOrder.thirdPartyDeliveryInfo.trackUrl}
                target="_blank"
                rel="noopener noreferrer">
                {t(
                  'ShopperOrders.openTrackingLink',
                  'Open tracking link in new tab',
                )}
              </a>
              <Box mt={1}>
                <iframe
                  src={currentOrder.thirdPartyDeliveryInfo.trackUrl}
                  width="100%"
                  height="400px"
                  style={{ border: '1px solid #ddd', borderRadius: '8px' }}
                  title="Delivery Tracking"
                />
              </Box>
            </Box>
          )}
      </Paper>

      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <Typography variant="h6">
          {t('ShopperOrders.products', 'Products')}
        </Typography>
        <List>
          {products.map((item) => {
            const prodName = (item.product && item.product.name) || 'Product';
            const prodImage =
              item.product && item.product.images && item.product.images[0]
                ? item.product.images[0].image
                : '';

            return (
              <React.Fragment key={item.id}>
                <ListItem>
                  <Checkbox
                    edge="start"
                    checked={item.taken || false}
                    onChange={() => handleToggleTaken(item)}
                    checkedIcon={<CheckCircleIcon style={{ color: 'green' }} />}
                    disabled={!isSelectingProducts}
                  />
                  <ListItemAvatar
                    style={{ minWidth: 'auto', marginRight: '16px' }}>
                    <Avatar
                      src={prodImage}
                      alt={prodName}
                      variant="rounded"
                      style={{
                        width: '90px',
                        height: '90px',
                        cursor: prodImage ? 'pointer' : 'default',
                        transition: 'transform 0.2s',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                      onClick={() => {
                        if (prodImage) setSelectedImage(prodImage);
                      }}
                      onMouseOver={(e) => {
                        if (prodImage) {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (prodImage) {
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={prodName}
                    secondary={
                      <React.Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          display="block">
                          Qty: {item.quantity}
                        </Typography>
                        {item.product && item.product.price !== undefined && (
                          <Tooltip title="The price may vary" placement="top">
                            <Typography
                              component="span"
                              variant="body2"
                              display="block"
                              style={{
                                color: 'gray',
                                cursor: 'help',
                                textDecoration: 'underline dotted',
                              }}>
                              Estimated price:{' '}
                              {(item.product.currency &&
                                item.product.currency.symbol) ||
                                '$'}
                              {item.product.price}
                            </Typography>
                          </Tooltip>
                        )}
                      </React.Fragment>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() =>
                        handleDeleteProduct(item.storeBranchProduct || item.id)
                      }
                      disabled={!isSelectingProducts}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            );
          })}
          {products.length === 0 && (
            <Typography variant="body2">
              {t('ShopperOrders.noProducts', 'No products in this order')}
            </Typography>
          )}
        </List>
      </Paper>

      {isPaying && (
        <Paper style={{ padding: '16px', marginBottom: '16px' }}>
          <Typography variant="h6">
            {t('ShopperOrders.payment', 'Payment & Receipt')}
            {isOrderPaid && (
              <CheckCircleIcon
                style={{
                  color: 'green',
                  verticalAlign: 'middle',
                  marginLeft: '8px',
                }}
              />
            )}
          </Typography>
          <Box mt={2}>
            <TextField
              label={t('ShopperOrders.totalValue', 'Total Value')}
              type="number"
              value={totalValue}
              onChange={(e) => {
                if (!isOrderPaid) setTotalValue(e.target.value);
              }}
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', readOnly: isOrderPaid }}
              disabled={isOrderPaid}
            />
            <TextField
              label={t('ShopperOrders.taxes', 'Taxes')}
              type="number"
              value={taxes}
              onChange={(e) => {
                if (!isOrderPaid) setTaxes(e.target.value);
              }}
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', readOnly: isOrderPaid }}
              disabled={isOrderPaid}
            />
          </Box>
          <Box mt={2}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={(e) => {
                if (!isOrderPaid) handleFileChange(e);
              }}
              disabled={isOrderPaid}
            />
            <label htmlFor={isOrderPaid ? '' : 'raised-button-file'}>
              <Button
                variant="outlined"
                component="span"
                disabled={isOrderPaid}>
                {t('ShopperOrders.uploadReceipt', 'Upload Main Receipt')}
              </Button>
            </label>
            {receiptBase64 && (
              <Typography variant="caption" display="block">
                Main receipt selected
              </Typography>
            )}
          </Box>
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePayment}
              disabled={isOrderPaid || totalValue === '' || taxes === ''}>
              {t('ShopperOrders.pay', 'Pay & Complete')}
            </Button>
          </Box>
        </Paper>
      )}

      {isOrderPaid && (
        <Paper style={{ padding: '16px' }}>
          <Typography variant="h6">
            {t('ShopperOrders.extraReceipts', 'Extra Receipts')}
          </Typography>
          <Box mt={2}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="extra-receipts-file"
              type="file"
              multiple
              onChange={handleExtraFilesChange}
            />
            <label htmlFor="extra-receipts-file">
              <Button variant="outlined" component="span">
                {t('ShopperOrders.selectExtra', 'Select Extra Receipts')}
              </Button>
            </label>
            {extraReceipts.length > 0 && (
              <Typography variant="caption" display="block">
                {extraReceipts.length + ' extra receipts selected'}
              </Typography>
            )}
          </Box>
          <Box mt={2}>
            <Button
              variant="contained"
              onClick={handleUploadExtra}
              disabled={extraReceipts.length === 0}>
              {t('ShopperOrders.uploadExtra', 'Upload Extra')}
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
          },
        }}>
        <DialogContent
          style={{
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full view"
              style={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                backgroundColor: '#fff',
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showPaymentConfirmation}
        onClose={() => setShowPaymentConfirmation(false)}>
        <DialogTitle>
          {t('ShopperOrders.confirmPaymentTitle', 'Confirm Payment')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ShopperOrders.confirmPaymentMessage', 'The total amount is')} $
            {parseFloat(totalValue || 0).toFixed(2)}.{' '}
            {t('ShopperOrders.areYouSure', 'Are you sure?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPaymentConfirmation(false)}
            color="default">
            {t('ShopperOrders.no', 'No')}
          </Button>
          <Button
            onClick={() => {
              setShowPaymentConfirmation(false);
              executePayment();
            }}
            color="primary"
            variant="contained"
            autoFocus>
            {t('ShopperOrders.yes', 'Yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};
